
  import { defineComponent, ref } from 'vue'
  import { mapActions, mapState } from 'vuex'
  import { PartnerService } from '@/services/PartnerService'
  import { IntegrationService } from '@/services/IntegrationService'
  import {
    ShopOutlined,
    UserOutlined,
    CopyOutlined,
    ReadOutlined,
    InfoCircleOutlined,
    KeyOutlined,
    CrownOutlined,
    SolutionOutlined
  } from '@ant-design/icons-vue'
  import { notification } from 'ant-design-vue'
  import { TableState, TableStateFilters } from 'ant-design-vue/es/table/interface'

  type Pagination = TableState['pagination'];

  export default defineComponent({
    name: 'PartnerCustomersDatatable',
    props: {
      partnerHash: String,
      isFeatureApi: Boolean,
      search: {
        type: String,
        default: ''
      }
    },
    emits: ['turn-partner', 'view-partner'],
    components: {
      ShopOutlined,
      UserOutlined,
      CopyOutlined,
      ReadOutlined,
      InfoCircleOutlined,
      KeyOutlined,
      CrownOutlined,
      SolutionOutlined
    },
    watch: {
      partnerHash: async function (newPartnerHash) {
        if (newPartnerHash !== '') {
          await this.fetchPartnersPlans(newPartnerHash)
          await this.fetchPartnerCustomers(newPartnerHash, 1, this.pageSize, null, null)
        }
      },
      search: async function () {
        this.currentPage = 1

        await this.fetchPartnerCustomers(
          this.partnerHash,
          1,
          this.pageSize,
          null,
          null
        )
      }
    },
    computed: {
      ...mapState('configuracao', ['partnerCustomers', 'isLoadingPartnerCustomers']),
      listPartnerCustomers (): any[] {
        return this.partnerCustomers !== null ? this.partnerCustomers : []
      }
    },
    data: () => ({
      showActions: ref<boolean>(true),
      statusFilterList: ref<any>(null),
      columns: [
        {
          title: 'Cliente',
          dataIndex: 'name',
          key: 'name',
          slots: { customRender: 'name' },
          width: '32%'
        },
        {
          title: 'Vencimento',
          dataIndex: 'expirationDate',
          slots: { customRender: 'expirationDate' },
          key: 'expirationDate',
          sorter: true,
          sortDirections: ['descend', 'ascend'],
          width: '22%'
        },
        {
          title: 'Status',
          dataIndex: 'status',
          slots: { customRender: 'status' },
          filters: [
            {
              text: 'Ativo',
              value: 'Active'
            },
            {
              text: 'Vencido',
              value: 'Expired'
            },
            {
              text: 'Pendente',
              value: 'Pending'
            }
          ],
          width: '18%'
        },
        {
          title: 'Plano',
          dataIndex: 'plan',
          slots: { customRender: 'plan' },
          key: 'plan',
          sorter: false,
          filters: [],
          width: '18%'
        },
        {
          title: 'Ações',
          dataIndex: 'action',
          slots: { customRender: 'action' },
          key: 'action',
          sorter: false,
          filters: [],
          fixed: 'right',
          width: '10%'
        }
      ],
      partnerService: ref<PartnerService>(),
      customLocale: {
        filterConfirm: 'Ok',
        filterReset: 'Limpar',
        sortTitle: 'Ordenar',
        filterTitle: 'Filtrar'
      },
      isShowKeysClient: false,
      clientHash: ref(''),
      currentPage: 1,
      pageSize: 6,
      total: 0,
      highlightId: null as number | null,
      lastSorter: null as string | null,
      lastStatus: null as string | null
    }),
    methods: {
      ...mapActions('configuracao', ['setPartnerCustomers', 'setLoadingPartnerCustomers']),
      rowClassName (record: any) {
        let classes = ''

        if (record.isPartner) {
          classes += ' partner-row'
        }

        if (record.id === this.highlightId) {
          classes += ' highlight-partner'
        }

        return classes.trim()
      },
      async fetchPartnerCustomers (partnerHash: any, pageNumber: any, pageSize: any, order: any, filterStatus: any) {
        this.setLoadingPartnerCustomers(true)
        try {
          let response

          if (this.search && this.search.trim() !== '') {
            response =
              await PartnerService.getInstance()
                .searchPartnerCustomers(
                  partnerHash,
                  pageNumber,
                  pageSize,
                  this.search)
          } else {
            response =
              await PartnerService.getInstance()
                .getPartnerCustomers(
                  partnerHash,
                  pageNumber,
                  pageSize,
                  order,
                  filterStatus)
          }

          if (response) {
            this.currentPage = response.pageNumber
            this.pageSize = response.pageSize
            this.total = response.total

            this.mountPartnerCustomers(response.items)
          } else {
            this.setPartnerCustomers([])
          }
        } finally {
          this.setLoadingPartnerCustomers(false)
        }
      },
      async mountPartnerCustomers (customers: any[]) {
        const partnerCustomers = [] as any
        customers.forEach((element: any) => {
          let status = ''

          switch (element.status) {
            case 1:
              status = 'Ativo'
              break
            case 2:
              status = 'Vencido'
              break
            case 3:
              status = 'Pendente'
              break
          }

          this.statusFilterList = {
            text: status,
            value: element.status
          }
          const it = {
            name: element.customerName,
            expirationDate: element.expiredAt === null ? '--/--/----' : element.expiredAt,
            status: status,
            plan: element.plan,
            accountType: element.accountType,
            id: element.customersAccountId,
            isPartner: !!element.isPartner
          }
          partnerCustomers.push(it)
        })

        this.setPartnerCustomers(partnerCustomers)
      },
      async handleTablePartner (pag: Pagination, filters: TableStateFilters, sorter: any) {
        let status = null

        if (filters.status !== undefined) {
          status = filters.status.join(',')
        }

        this.currentPage = pag?.current ?? 1
        this.pageSize = pag?.pageSize ?? 6

        this.lastSorter = sorter.order ?? null
        this.lastStatus = status

        await this.fetchPartnerCustomers(
          this.partnerHash,
          this.currentPage,
          this.pageSize,
          this.lastSorter,
          this.lastStatus)
      },
      async fetchPartnersPlans (partnerHash:string) {
        const plans = await PartnerService.getInstance().getPlansPartner(partnerHash)
        this.columns[this.columns.length - 2].filters = plans.map(x => ({ text: x.name, value: x.id.toString() }))
      },
      onTurnPartner (record: any) {
        this.$emit('turn-partner', record)
      },
      onViewPartner (record: any) {
        this.$emit('view-partner', record)
      },
      async showModalKeysPartnerCustomer (item: any, partnerHash: any) {
        const response = await IntegrationService.getInstance().getIntegrationPartnerClientData(partnerHash, item.id)
        if (response) {
          this.isShowKeysClient = true
          this.clientHash = response.clientHash
        } else {
          this.isShowKeysClient = false
          this.clientHash = ''
        }
      },
      async copyClientHash () {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(this.clientHash)
          notification.success({
            message: 'O X-Client Id foi copiado com sucesso!'
          })
        } else {
          const textarea = document.createElement('textarea')
          textarea.value = this.clientHash
          textarea.style.position = 'absolute'
          textarea.style.left = '-99999999px'
          document.body.prepend(textarea)
          textarea.select()
          try {
            document.execCommand('copy')
          } catch (err) {
            console.error(err)
          } finally {
            textarea.remove()
            notification.success({
              message: 'O X-Client Id foi copiado com sucesso!'
            })
          }
        }
      },
      async reload (highlightId?: number) {
        this.highlightId = highlightId ?? null

        await this.fetchPartnerCustomers(
          this.partnerHash,
          this.currentPage,
          this.pageSize,
          this.lastSorter,
          this.lastStatus)

        if (highlightId) {
          setTimeout(() => {
            this.highlightId = null
          }, 1800)
        }
      }
    }
  })
